import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import type { CreateAddressRequest } from "../../api/addresses/types";
import "./CreateAddressPopover.css";

export interface CreateAddressPopoverProps {
    position: { x: number; y: number };
    latitude: number;
    longitude: number;
    onClose: () => void;
    onCreate: (request: CreateAddressRequest) => Promise<void>;
    isLoading?: boolean;
}

export const CreateAddressPopover = ({
    position,
    latitude,
    longitude,
    onClose,
    onCreate,
    isLoading = false
}: CreateAddressPopoverProps): JSX.Element => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<CreateAddressRequest>({
        latitude,
        longitude,
        street: "",
        number: null,
        apartment: null,
        city: "",
        postalCode: ""
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const popoverRef = useRef<HTMLDivElement>(null);

    // Adjust position to keep popover within viewport
    useEffect(() => {
        if (popoverRef.current) {
            const rect = popoverRef.current.getBoundingClientRect();
            let adjustedX = position.x;
            let adjustedY = position.y;

            if (rect.right > window.innerWidth) {
                adjustedX = window.innerWidth - rect.width - 10;
            }
            if (rect.bottom > window.innerHeight) {
                adjustedY = window.innerHeight - rect.height - 10;
            }
            if (adjustedX < 0) adjustedX = 10;
            if (adjustedY < 0) adjustedY = 10;

            if (adjustedX !== position.x || adjustedY !== position.y) {
                popoverRef.current.style.left = `${adjustedX}px`;
                popoverRef.current.style.top = `${adjustedY}px`;
            }
        }
    }, [position]);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.street || formData.street.trim().length === 0) {
            newErrors.street = t("map.createAddressPopover.errors.streetRequired");
        }

        if (!formData.city || formData.city.trim().length === 0) {
            newErrors.city = t("map.createAddressPopover.errors.cityRequired");
        }

        if (!formData.postalCode || formData.postalCode.trim().length === 0) {
            newErrors.postalCode = t("map.createAddressPopover.errors.postalCodeRequired");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        try {
            await onCreate(formData);
            toast.success(t("map.createAddressPopover.success"));
            onClose();
        } catch (error) {
            console.error("Failed to create address:", error);
            const errorMessage = error instanceof Error ? error.message : t("map.createAddressPopover.error");
            toast.error(errorMessage);
        }
    };

    return (
        <div
            ref={popoverRef}
            className="create-address-popover"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`
            }}
            role="dialog"
            aria-labelledby="create-address-popover__title"
            aria-modal="true"
        >
            <header className="create-address-popover__header">
                <h3 id="create-address-popover__title" className="create-address-popover__title">
                    {t("map.createAddressPopover.title")}
                </h3>
                <button
                    type="button"
                    className="create-address-popover__close"
                    onClick={onClose}
                    aria-label={t("common.close")}
                >
                    ×
                </button>
            </header>

            <form onSubmit={handleSubmit} className="create-address-popover__form">
                <div className="create-address-popover__body">
                    <div className="create-address-popover__field">
                        <label htmlFor="create-street" className="create-address-popover__label">
                            {t("map.createAddressPopover.street")} *
                        </label>
                        <input
                            id="create-street"
                            type="text"
                            value={formData.street}
                            onChange={(e) =>
                                setFormData({ ...formData, street: e.target.value })
                            }
                            disabled={isLoading}
                            className={`create-address-popover__input ${errors.street ? "create-address-popover__input--error" : ""}`}
                            autoFocus
                            aria-invalid={!!errors.street}
                            aria-describedby={errors.street ? "create-street-error" : undefined}
                        />
                        {errors.street && (
                            <span id="create-street-error" className="create-address-popover__error">
                                {errors.street}
                            </span>
                        )}
                    </div>

                    <div className="create-address-popover__field">
                        <label htmlFor="create-number" className="create-address-popover__label">
                            {t("map.createAddressPopover.number")}
                        </label>
                        <input
                            id="create-number"
                            type="text"
                            value={formData.number ?? ""}
                            onChange={(e) =>
                                setFormData({ ...formData, number: e.target.value || null })
                            }
                            disabled={isLoading}
                            className="create-address-popover__input"
                        />
                    </div>

                    <div className="create-address-popover__field">
                        <label htmlFor="create-apartment" className="create-address-popover__label">
                            {t("map.createAddressPopover.apartment")}
                        </label>
                        <input
                            id="create-apartment"
                            type="text"
                            value={formData.apartment ?? ""}
                            onChange={(e) =>
                                setFormData({ ...formData, apartment: e.target.value || null })
                            }
                            disabled={isLoading}
                            className="create-address-popover__input"
                        />
                    </div>

                    <div className="create-address-popover__field">
                        <label htmlFor="create-city" className="create-address-popover__label">
                            {t("map.createAddressPopover.city")} *
                        </label>
                        <input
                            id="create-city"
                            type="text"
                            value={formData.city}
                            onChange={(e) =>
                                setFormData({ ...formData, city: e.target.value })
                            }
                            disabled={isLoading}
                            className={`create-address-popover__input ${errors.city ? "create-address-popover__input--error" : ""}`}
                            aria-invalid={!!errors.city}
                            aria-describedby={errors.city ? "create-city-error" : undefined}
                        />
                        {errors.city && (
                            <span id="create-city-error" className="create-address-popover__error">
                                {errors.city}
                            </span>
                        )}
                    </div>

                    <div className="create-address-popover__field">
                        <label htmlFor="create-postalCode" className="create-address-popover__label">
                            {t("map.createAddressPopover.postalCode")} *
                        </label>
                        <input
                            id="create-postalCode"
                            type="text"
                            value={formData.postalCode}
                            onChange={(e) =>
                                setFormData({ ...formData, postalCode: e.target.value })
                            }
                            disabled={isLoading}
                            className={`create-address-popover__input ${errors.postalCode ? "create-address-popover__input--error" : ""}`}
                            aria-invalid={!!errors.postalCode}
                            aria-describedby={errors.postalCode ? "create-postalCode-error" : undefined}
                        />
                        {errors.postalCode && (
                            <span id="create-postalCode-error" className="create-address-popover__error">
                                {errors.postalCode}
                            </span>
                        )}
                    </div>

                    <div className="create-address-popover__coordinates">
                        <small>
                            {t("map.createAddressPopover.coordinates")}: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                        </small>
                    </div>
                </div>

                <footer className="create-address-popover__footer">
                    <button
                        type="button"
                        className="create-address-popover__button create-address-popover__button--cancel"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="submit"
                        className="create-address-popover__button create-address-popover__button--create"
                        disabled={isLoading}
                    >
                        {isLoading ? t("common.loading") : t("common.create")}
                    </button>
                </footer>
            </form>
        </div>
    );
};